import type {
  Artist,
  BrowseData,
  DefaultResponseBody,
  ExploreData,
  ListResponseBody,
  NewReleasesSection,
  PodcastsHome,
  Song,
  TopArtist,
  Video,
} from "@gerama/types";
import type { ApiClient } from "../client";

const empty = { list: [], count: 0 };

/** Aggregated, server-cached read models behind Explore / Browse / New Releases / Podcasts, plus the signed-in "My Music" lists. */
export function createCoreDiscoverApi(client: ApiClient) {
  return {
    explore: (signal?: AbortSignal) =>
      client
        .request<DefaultResponseBody<ExploreData>>("/discover/explore", { signal })
        .then((res) => res.data as ExploreData),

    browse: (signal?: AbortSignal) =>
      client
        .request<DefaultResponseBody<BrowseData>>("/discover/browse", { signal })
        .then((res) => res.data as BrowseData),

    topArtists: (params: { page?: number; limit?: number } = {}, signal?: AbortSignal) =>
      client
        .request<ListResponseBody<TopArtist>>("/discover/top-artists", {
          query: { ...params },
          signal,
        })
        .then((res) => res.data ?? empty),

    newReleases: (params: { page?: number; limit?: number } = {}, signal?: AbortSignal) =>
      client
        .request<ListResponseBody<NewReleasesSection>>("/discover/new-releases", {
          query: { ...params },
          signal,
        })
        .then((res) => res.data ?? empty),

    podcasts: (params: { page?: number; limit?: number } = {}, signal?: AbortSignal) =>
      client
        .request<DefaultResponseBody<PodcastsHome>>("/discover/podcasts", {
          query: { ...params },
          signal,
        })
        .then((res) => res.data as PodcastsHome),

    /** My Music (signed in). */
    library: {
      songs: (params: { page?: number; limit?: number } = {}, signal?: AbortSignal) =>
        client
          .request<ListResponseBody<Song>>("/library/songs", { query: { ...params }, signal })
          .then((res) => res.data ?? empty),
      likedSongs: (params: { page?: number; limit?: number } = {}, signal?: AbortSignal) =>
        client
          .request<ListResponseBody<Song>>("/library/liked-songs", { query: { ...params }, signal })
          .then((res) => res.data ?? empty),
      videos: (params: { page?: number; limit?: number } = {}, signal?: AbortSignal) =>
        client
          .request<ListResponseBody<Video>>("/library/videos", { query: { ...params }, signal })
          .then((res) => res.data ?? empty),
      albums: (params: { page?: number; limit?: number } = {}, signal?: AbortSignal) =>
        client
          .request<ListResponseBody<{ id: number; name: string; cover: string | null }>>(
            "/library/albums",
            {
              query: { ...params },
              signal,
            },
          )
          .then((res) => res.data ?? empty),
      artists: (params: { page?: number; limit?: number } = {}, signal?: AbortSignal) =>
        client
          .request<ListResponseBody<Artist>>("/interaction/artists/followed", {
            query: { ...params },
            signal,
          })
          .then((res) => res.data ?? empty),
    },
  };
}
